import { AccountRole } from '@/data/usecases/account/account-role'
import { AccountType } from '@/data/usecases/account/account-type'
import { ContractedPlanLicense } from '@/data/usecases/plan-license/contracted-plan-license'
import { Account } from '@/domain/usecases'
import { AccountBenefit } from '@/domain/usecases/account/account-benefit'
import { AccountProfessional } from '@/domain/usecases/account/account-professional'
import { CompanyManagerAccount } from '@/domain/usecases/account/company-manager/company-manager-account-profile'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import CompanyManagerBaseLoader from '@/main/routes/loaders/company-manager-base-loader'
import PathConstants from '@/main/routes/path-contants'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import { LoaderFunctionArgs } from 'react-router-dom'
import {
  UserProfileLoaderBenefitOptionsType,
  UserProfileLoaderDataType,
  UserProfileLoaderProfessionalOptionsType
} from '../../user/user-profile-loader'

export type CompanyManagerUserProfileLoaderDataType = UserProfileLoaderDataType & {
  accountStatus: CompanyManagerAccount.StatusModel
  accountInactivationReasons: CompanyManagerAccount.InactivationReasonListModel
  ownProfile: boolean
}

export default class CompanyManagerUserProfileLoader extends CompanyManagerBaseLoader {
  private static readonly ACCOUNT_ID_PATH_PARAM_NAME = 'accountId'

  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined,
    readonly accountType?: AccountType.Enum | null,
    readonly redirectUrl: string = PathConstants.Routes.USER_PROFILE.value
  ) {
    super(account, redirectUrl, queryClient, ldClient)
  }

  private async getAccountProfileData(accountId: string) {
    return import('@remote/account/company-manager/remote-company-manager-account-profile-factory')
      .then(module => module.makeRemoteCompanyManagerAccountProfile)
      .then(async makeRemoteCompanyManagerAccountProfile => {
        const remote = makeRemoteCompanyManagerAccountProfile(accountId, this.queryClient)
        return Promise.all([
          remote.profile(),
          remote.getStatus(),
          remote.getInactivationReasons()
        ]).then(([accountProfile, accountStatus, accountInactivationReasons]) => {
          return { accountProfile, accountStatus, accountInactivationReasons }
        })
      })
  }

  private async getProfessionalOptions(): Promise<
    UserProfileLoaderProfessionalOptionsType | undefined
  > {
    if (AccountType.Enum.DEPENDENT.is(this.account)) return undefined
    return super
      .importRemoteModule('makeRemoteOnboardingOptions')
      .then(async makeRemoteOnboardingOptions => {
        const remoteOnboardingOptions = makeRemoteOnboardingOptions(
          this.queryClient
        ) as OnboardingOptions
        return remoteOnboardingOptions.getStep1()
      })
      .then(data => {
        return {
          areas: data.professionalAreas,
          seniorities: data.professionalSeniorities
        }
      })
  }

  private async getAccountProfessional(
    accountProfile: Account.ProfileModel
  ): Promise<AccountProfessional.Model | undefined> {
    if (AccountType.Enum.DEPENDENT.is(accountProfile)) return undefined
    return super.importRemoteModule('makeRemoteAccountProfessional').then(async remote => {
      return (remote(this.queryClient) as AccountProfessional).get(accountProfile.id)
    })
  }

  private async getBenefitsOptions(
    accountProfile: Account.ProfileModel,
    accountBenefits: AccountBenefit.Model
  ): Promise<AccountBenefit.OptionsModel> {
    const holderId = accountBenefits.contractedPlan.holder?.id
    if (holderId) return await this.getDependentBenefitsOptions(holderId)
    return await this.getCollaboratorBenefitsOptions(accountProfile)
  }

  private async getCollaboratorBenefitsOptions(
    accountProfile: Account.ProfileModel
  ): Promise<AccountBenefit.OptionsModel> {
    return import('@remote/account/company-manager/remote-company-manager-plan-license-factory')
      .then(module => module.makeRemoteCompanyPlanLicense)
      .then(async makeRemotePlanLicense => {
        const remote = makeRemotePlanLicense(accountProfile.company.uniqueId, this.queryClient)
        return remote.loader()
      })
      .then(licenses => {
        return {
          passPlans: licenses
            .filter(license => accountProfile.accountType === license.accountType)
            .map(license => ({
              id: license.id,
              name: license.name,
              contracted: !!license.available
            })) as ContractedPlanLicense.ListModel
        }
      })
  }

  private async getDependentBenefitsOptions(accountId: string) {
    return import(
      '@remote/account/company-manager/remote-company-manager-dependent-registration-options-factory'
    )
      .then(module => module.makeRemoteCompanyManagerDependentRegistrationOptions)
      .then(async makeRemoteCompanyManagerAccountProfile => {
        const remote = makeRemoteCompanyManagerAccountProfile(accountId, this.queryClient)
        return remote.loader()
      })
  }

  private async getAccountBenefits(
    accountProfile: Account.ProfileModel
  ): Promise<UserProfileLoaderBenefitOptionsType> {
    return super.importRemoteModule('makeRemoteAccountProfile').then(async remote => {
      const remoteBenefits = remote(this.queryClient) as Account
      return remoteBenefits.getBenefits(accountProfile.id).then(async accountBenefits => {
        return {
          accountBenefits,
          benefitsOptions: await this.getBenefitsOptions(accountProfile, accountBenefits)
        }
      })
    })
  }

  private verifyPermission(accountProfile: Account.ProfileModel): boolean {
    if (this.accountType && !this.accountType.is(accountProfile)) return false

    return (
      (AccountType.Enum.DEPENDENT.is(accountProfile) &&
        FeatureFlag.Key.RH_EDIT_DEPENDENT_PROFILE.getValue(this.ldClient)) ||
      (AccountType.Enum.COLLABORATOR.is(accountProfile) &&
        FeatureFlag.Key.RH_EDIT_COLLABORATOR_PROFILE.getValue(this.ldClient))
    )
  }

  async loader({ params }: LoaderFunctionArgs) {
    if (AccountRole.Enum.COMPANY_MANAGER.isNot(this.account)) {
      return super.notAllowedLoader()
    }

    const accountId = params[CompanyManagerUserProfileLoader.ACCOUNT_ID_PATH_PARAM_NAME]
    if (!accountId) return null

    const { accountProfile, accountStatus, accountInactivationReasons } =
      await this.getAccountProfileData(accountId)
    if (!this.verifyPermission(accountProfile)) {
      return super.notAllowedLoader()
    }

    return this.deferExec({
      data: Promise.all([
        this.getAccountProfessional(accountProfile),
        this.getAccountBenefits(accountProfile),
        this.getProfessionalOptions()
      ]).then(
        ([accountProfessional, { accountBenefits, benefitsOptions }, professionalOptions]) => ({
          accountProfile,
          accountStatus,
          accountInactivationReasons,
          accountProfessional,
          accountBenefits,
          benefitsOptions,
          professionalOptions,
          ownProfile: accountId === this.account.id
        })
      )
    })
  }
}
